import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Modal from "react-modal";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { ReactComponent as CloseIcon } from "../assets/icons/x.svg";
import { VisaAdminService } from "../api/generated/services/VisaAdminService";
import { GetTransactionHistoryByIdResult } from "../api/generated/models/GetTransactionHistoryByIdResult";
import { TransactionHistorySuccessDto } from "../api/generated/models/TransactionHistorySuccessDto";
import { TransactionHistoryFailureDto } from "../api/generated/models/TransactionHistoryFailureDto";
import { TransactionHistoryVisaEventDto } from "../api/generated/models/TransactionHistoryVisaEventDto";
import { ROUTES } from "../config/routes";
import "../api/openApiConfig";

interface ParsedJsonData {
  identityId?: string;
  contentfulBusinessId?: string;
  transactionTimestamp?: string;
  cardNumberLastFour?: string;
  transactionAmount?: number;
  transactionCurrency?: string;
}

const parseJsonSentToBackend = (json: string | null | undefined): ParsedJsonData => {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json);
    return {
      identityId: parsed.identityId || undefined,
      contentfulBusinessId: parsed.contentfulBusinessId || undefined,
      transactionTimestamp: parsed.transactionTimestamp || undefined,
      cardNumberLastFour: parsed.cardNumberLastFour || undefined,
      transactionAmount: parsed.transactionAmount || undefined,
      transactionCurrency: parsed.transactionCurrency || undefined,
    };
  } catch {
    return {};
  }
};

const formatDateTime = (dateString: string | undefined): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleString("da-DK", {
      timeZone: "Europe/Copenhagen",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return dateString;
  }
};

const formatAmount = (amount: number | undefined, currency: string | undefined): string => {
  if (amount === undefined) return "-";
  const currencyStr = currency || "";
  return `${amount.toFixed(2)} ${currencyStr}`.trim();
};

const formatJson = (jsonString: string | null | undefined): string => {
  if (!jsonString) return "No data";
  try {
    const parsed = JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return jsonString;
  }
};

const AdminVisaTransaction: React.FC = () => {
  const [searchParams] = useSearchParams();
  const uniqueTransactionId = searchParams.get("uniqueTransactionId");

  const [transaction, setTransaction] = useState<GetTransactionHistoryByIdResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [visaEventModalOpen, setVisaEventModalOpen] = useState(false);
  const [selectedVisaEvent, setSelectedVisaEvent] = useState<TransactionHistoryVisaEventDto | null>(null);

  useEffect(() => {
    if (uniqueTransactionId) {
      fetchTransaction();
    } else {
      setError("No uniqueTransactionId provided");
      setLoading(false);
    }
  }, [uniqueTransactionId]);

  const fetchTransaction = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await VisaAdminService.postApiVisaAdminGetTransactionHistoryById({
        uniqueTransactionId,
      });
      setTransaction(result);
    } catch (err) {
      setError("Failed to fetch transaction details");
      console.error("Error fetching transaction:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenVisaEventModal = (visaEventId: string | null | undefined) => {
    if (!visaEventId || !transaction?.visaEvents) {
      return;
    }
    const visaEvent = transaction.visaEvents[visaEventId];
    if (visaEvent) {
      setSelectedVisaEvent(visaEvent);
      setVisaEventModalOpen(true);
    }
  };

  const handleCloseVisaEventModal = () => {
    setVisaEventModalOpen(false);
    setSelectedVisaEvent(null);
  };

  if (loading) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  if (error || !transaction) {
    return (
      <div>
        <div className="mb-8 lg:mb-12">
          <h2 className="text-black/60">Visa Transaction Details</h2>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg mb-4">
            {error || "Transaction not found"}
          </div>
          <Link to={ROUTES.ADMIN_VISA()}>
            <Button variant="outline">Back to Visa Admin</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 lg:mb-12">
        <h2 className="text-black/60">Visa Transaction Details</h2>
      </div>

      {/* Back button */}
      <div className="mb-4">
        <Link to={ROUTES.ADMIN_VISA()}>
          <Button variant="outline" size="sm">
            Back to Visa Admin
          </Button>
        </Link>
      </div>

      {/* Transaction Overview */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Transaction Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-sm text-gray-500">Unique Transaction ID</div>
            <div className="font-mono text-sm break-all">{transaction.uniqueTransactionId || "-"}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Status</div>
            <div>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  transaction.status === "Succeeded"
                    ? "bg-green-100 text-green-800"
                    : transaction.status === "Failed"
                    ? "bg-red-100 text-red-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {transaction.status || "-"}
              </span>
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Last Updated At</div>
            <div>{formatDateTime(transaction.lastUpdatedAt)}</div>
          </div>
        </div>
      </div>

      {/* Successes */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Successes ({transaction.successes?.length || 0})
        </h3>
        {!transaction.successes || transaction.successes.length === 0 ? (
          <div className="text-gray-500 text-sm">No successful attempts</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Succeeded At</th>
                  <th>Identity ID</th>
                  <th>Business ID</th>
                  <th>Tx Timestamp</th>
                  <th>Card Last 4</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {transaction.successes.map((success: TransactionHistorySuccessDto, index: number) => {
                  const jsonData = parseJsonSentToBackend(success.jsonSentToPrimaryBackend);
                  return (
                    <tr key={index}>
                      <td className="whitespace-nowrap">{formatDateTime(success.succeededAt)}</td>
                      <td className="font-mono text-xs whitespace-nowrap">{jsonData.identityId || "-"}</td>
                      <td className="font-mono text-xs whitespace-nowrap">{jsonData.contentfulBusinessId || "-"}</td>
                      <td className="whitespace-nowrap">{formatDateTime(jsonData.transactionTimestamp)}</td>
                      <td className="font-mono whitespace-nowrap">{jsonData.cardNumberLastFour || "-"}</td>
                      <td className="whitespace-nowrap">
                        {formatAmount(jsonData.transactionAmount, jsonData.transactionCurrency)}
                      </td>
                      <td className="whitespace-nowrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenVisaEventModal(success.visaEventId)}
                          disabled={!success.visaEventId}
                        >
                          Visa Event
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Failures */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Failures ({transaction.failures?.length || 0})
        </h3>
        {!transaction.failures || transaction.failures.length === 0 ? (
          <div className="text-gray-500 text-sm">No failed attempts</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Failed At</th>
                  <th>Error Code</th>
                  <th>Error Message</th>
                  <th>Identity ID</th>
                  <th>Business ID</th>
                  <th>Tx Timestamp</th>
                  <th>Card Last 4</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {transaction.failures.map((failure: TransactionHistoryFailureDto, index: number) => {
                  const jsonData = parseJsonSentToBackend(failure.jsonSentToPrimaryBackend);
                  return (
                    <tr key={index}>
                      <td className="whitespace-nowrap">{formatDateTime(failure.failedAt)}</td>
                      <td className="whitespace-nowrap">{failure.errorCode || "-"}</td>
                      <td className="whitespace-nowrap">{failure.errorMessage || "-"}</td>
                      <td className="font-mono text-xs whitespace-nowrap">{jsonData.identityId || "-"}</td>
                      <td className="font-mono text-xs whitespace-nowrap">{jsonData.contentfulBusinessId || "-"}</td>
                      <td className="whitespace-nowrap">{formatDateTime(jsonData.transactionTimestamp)}</td>
                      <td className="font-mono whitespace-nowrap">{jsonData.cardNumberLastFour || "-"}</td>
                      <td className="whitespace-nowrap">
                        {formatAmount(jsonData.transactionAmount, jsonData.transactionCurrency)}
                      </td>
                      <td className="whitespace-nowrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenVisaEventModal(failure.visaEventId)}
                          disabled={!failure.visaEventId}
                        >
                          Visa Event
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Visa Event Modal */}
      <Modal
        className="modal-content md:max-w-[800px]"
        overlayClassName="modal-overlay"
        isOpen={visaEventModalOpen}
        onRequestClose={handleCloseVisaEventModal}
        contentLabel="Visa Event Details"
      >
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-primary font-medium text-xl">Visa Event Details</h2>
          <Button onClick={handleCloseVisaEventModal} variant="icon">
            <CloseIcon className="w-3 h-3" />
          </Button>
        </div>

        {selectedVisaEvent && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500">Event ID</div>
                <div className="font-mono text-sm break-all">{selectedVisaEvent.eventId || "-"}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Received At</div>
                <div>{formatDateTime(selectedVisaEvent.receivedAt)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Version</div>
                <div>{selectedVisaEvent.version ?? "-"}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Is Processed</div>
                <div>{selectedVisaEvent.isProcessed ? "Yes" : "No"}</div>
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-500 mb-2">Event Data</div>
              <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto text-xs font-mono max-h-[400px] overflow-y-auto">
                {formatJson(selectedVisaEvent.eventData)}
              </pre>
            </div>
          </div>
        )}

        <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
          <Button variant="outline" onClick={handleCloseVisaEventModal}>
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminVisaTransaction;
